import AppLayout from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StrandBadge from "@/components/StrandBadge";
import ModeBadge from "@/components/ModeBadge";
import TranscriptViewer from "@/components/TranscriptViewer";
import { StatusIndicator } from "@/components/StatusIndicator";
import { ArrowLeft, CheckCircle, Edit3, Clock, Play, Pause, ChevronDown } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useEffect, useMemo, useRef } from "react";
import {
  useAssessment,
  useAssessmentDraftReport,
  useAssessmentEvidence,
  useAssessmentSignedReport,
  useSignOffAssessment,
  useStartReview,
  useRecordingUrl,
  useAssessmentTranscript,
  useAssessmentStatus,
  useDownloadReportPdf,
  type DraftReport,
  type AssessmentStatus,
} from "@/lib/api";
import { Download } from "lucide-react";
import { formatTime, ensureFiniteDuration } from "@/lib/audio";


export default function TeacherReviewPage() {
  const { id } = useParams<{ id: string }>();
  const assessmentId = id || "";
  const { data: assessment, isLoading: loadingAssessment } = useAssessment(assessmentId);
  const { data: draftReport, isLoading: loadingReport } = useAssessmentDraftReport(assessmentId);
  const { data: evidence, isLoading: loadingEvidence } = useAssessmentEvidence(assessmentId);
  const { data: recordingUrl, isLoading: loadingRecording } = useRecordingUrl(assessmentId);
  const { data: transcript, isLoading: loadingTranscript } = useAssessmentTranscript(assessmentId);
  const { status: wsStatus, message: wsMessage } = useAssessmentStatus(assessmentId);
  const signOffMutation = useSignOffAssessment();
  const startReviewMutation = useStartReview();
  const { data: signedReport } = useAssessmentSignedReport(assessmentId);
  const downloadPdf = useDownloadReportPdf();

  const currentStatus = (wsStatus || assessment?.status as AssessmentStatus | null);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);

  const resolvedRecordingUrl = useMemo(() => {
    if (!recordingUrl) return null;
    return recordingUrl
      .replace("http://minio:9000", "http://localhost:9000")
      .replace("https://minio:9000", "http://localhost:9000");
  }, [recordingUrl]);

  const mediaMimeType = useMemo(() => {
    if (!resolvedRecordingUrl) return null;

    const deriveExtension = (value: string) => {
      try {
        const parsed = new URL(value);
        const pathname = parsed.pathname || "";
        return pathname.split(".").pop()?.toLowerCase() || null;
      } catch {
        const sanitized = value.split("?")[0] || "";
        return sanitized.split(".").pop()?.toLowerCase() || null;
      }
    };

    const extension = deriveExtension(resolvedRecordingUrl);
    if (!extension) return null;

    const audioTypes: Record<string, string> = {
      wav: "audio/wav",
      mp3: "audio/mpeg",
      m4a: "audio/mp4",
      mp4a: "audio/mp4",
      aac: "audio/aac",
      oga: "audio/ogg",
      ogg: "audio/ogg",
      opus: "audio/ogg",
      flac: "audio/flac",
      weba: "audio/webm",
      webma: "audio/webm",
    };

    const videoTypes: Record<string, string> = {
      mp4: "video/mp4",
      m4v: "video/mp4",
      mov: "video/quicktime",
      webm: "video/webm",
    };

    if (audioTypes[extension]) return audioTypes[extension];
    if (videoTypes[extension]) return videoTypes[extension];

    // Default assumption: webm without extension mapping treated as video
    if (extension === "webm") return "video/webm";

    return null;
  }, [resolvedRecordingUrl]);

  const isVideoSource = useMemo(() => mediaMimeType?.startsWith("video/") ?? false, [mediaMimeType]);

  const [scores, setScores] = useState(
    ["physical", "linguistic", "cognitive", "social_emotional"].map((strand) => ({
      strand,
      aiScore: 0,
      teacherScore: null as number | null,
    }))
  );
  const [confirmed, setConfirmed] = useState(false);

  const bandToScore: Record<string, number> = {
    emerging: 1,
    developing: 2,
    growing: 3,
    effective: 4,
    accomplished: 5,
  };

  // Get score for a strand - physical uses deterministic 1-5 score, others use band mapping
  const getStrandScore = (strand: string, draftReport: DraftReport | undefined): number => {
    if (strand === "physical") {
      // Physical now has direct 1-5 score from deterministic scoring
      const physScore = draftReport?.physical_score as { score?: number; band?: string } | undefined;
      return physScore?.score ?? bandToScore[physScore?.band || "emerging"] ?? 1;
    }
    const scoreKey = `${strand}_score` as keyof typeof draftReport;
    const strandData = draftReport?.[scoreKey] as { band?: string } | undefined;
    return bandToScore[strandData?.band || "emerging"] || 1;
  };

  // Update scores when draft report is loaded
  useEffect(() => {
    if (draftReport) {
      setScores([
        {
          strand: "physical",
          aiScore: getStrandScore("physical", draftReport),
          teacherScore: null,
        },
        {
          strand: "linguistic",
          aiScore: getStrandScore("linguistic", draftReport),
          teacherScore: null,
        },
        {
          strand: "cognitive",
          aiScore: getStrandScore("cognitive", draftReport),
          teacherScore: null,
        },
        {
          strand: "social_emotional",
          aiScore: getStrandScore("social_emotional", draftReport),
          teacherScore: null,
        },
      ]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftReport]);

  const updateScore = (index: number, score: number) => {
    setScores((prev) =>
      prev.map((s, i) => (i === index ? { ...s, teacherScore: score } : s))
    );
  };

  const scoreToBand = (score: number | string): string => {
    if (typeof score === 'string') return score;
    if (score <= 1.5) return 'emerging';
    if (score <= 2.5) return 'developing';
    if (score <= 3.5) return 'growing';
    if (score <= 4.5) return 'effective';
    return 'accomplished';
  };

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    setPlaybackError(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    media.pause();
    try {
      media.currentTime = 0;
    } catch {
      // Ignore DOM exceptions when resetting currentTime before metadata is loaded
    }

    if (resolvedRecordingUrl) {
      media.load();
    }

    return () => {
      media.pause();
    };
  }, [resolvedRecordingUrl]);

  const playAudio = async () => {
    const media = mediaRef.current;
    if (!media) return;
    try {
      await media.play();
      setPlaybackError(null);
    } catch (error) {
      console.error("Failed to play recording:", error);
      setIsPlaying(false);
      setPlaybackError("Playback failed in this browser. You can open the recording in a new tab below.");
    }
  };

  const handleSeek = (time: number) => {
    const media = mediaRef.current;
    if (!media) return;
    const safeDuration = Number.isFinite(media.duration) && media.duration > 0 ? media.duration : duration;
    const clampedTime = safeDuration ? Math.min(Math.max(time, 0), safeDuration) : Math.max(time, 0);
    media.currentTime = clampedTime;
    if (media.paused) {
      void playAudio();
    }
  };

  const handlePlayPause = () => {
    const media = mediaRef.current;
    if (!media) return;
    if (isPlaying) {
      media.pause();
    } else {
      void playAudio();
    }
  };

  const handleSignOff = async () => {
    if (!id) return;
    try {
      await signOffMutation.mutateAsync({
        assessmentId: id,
        data: {
          physical_band: scoreToBand(scores[0].teacherScore || scores[0].aiScore),
          linguistic_band: scoreToBand(scores[1].teacherScore || scores[1].aiScore),
          cognitive_band: scoreToBand(scores[2].teacherScore || scores[2].aiScore),
          social_emotional_band: scoreToBand(scores[3].teacherScore || scores[3].aiScore),
        },
      });
      setConfirmed(true);
    } catch (error) {
      console.error("Failed to sign off:", error);
    }
  };

  const getModeBadgeMode = (mode: string): "presentational" | "exploratory" | "collaborative" | "dialogic" | "persuasive" | "reflective" => {
    switch (mode) {
      case "formal":
        return "presentational";
      case "informal":
        return "exploratory";
      case "practice":
        return "collaborative";
      default:
        return "exploratory";
    }
  };

  const getStrandLabel = (strand: string): string => {
    switch (strand) {
      case "physical":
        return "Physical";
      case "linguistic":
        return "Linguistic";
      case "cognitive":
        return "Cognitive";
      case "social_emotional":
        return "Social-Emotional";
      default:
        return strand;
    }
  };

  const loading = loadingAssessment || loadingReport || loadingEvidence;

  if (loading) {
    return (
      <AppLayout role="teacher">
        <div className="max-w-3xl mx-auto space-y-6">
          <Link to="/teacher" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              Loading assessment...
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (!assessment) {
    return (
      <AppLayout role="teacher">
        <div className="max-w-3xl mx-auto space-y-6">
          <Link to="/teacher" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              Assessment not found
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout role="teacher">
      <div className="max-w-3xl mx-auto space-y-6">
        <Link to="/teacher" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <span className="text-sm font-medium text-primary-foreground">
                {assessment.student_name?.split(" ").map(n => n[0]).join("") || "?"}
              </span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{assessment.student_name || "Student"} — {assessment.prompt_name || ""}</h1>
              <p className="text-xs text-muted-foreground">Submitted {assessment.uploaded_at || assessment.created_at ? new Date(assessment.uploaded_at || assessment.created_at).toLocaleString() : "N/A"}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <ModeBadge mode={getModeBadgeMode(assessment.mode)} />
            <StatusIndicator status={currentStatus} message={wsMessage} />
          </div>
        </motion.div>

        {/* Show message if no response yet */}
        {["pending", "uploading", "processing"].includes(currentStatus || assessment.status) && (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="flex flex-col items-center gap-3">
                <Clock className="w-12 h-12 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Waiting for student response</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  This assessment is currently waiting for the student to complete their response.
                  {wsMessage && <StatusIndicator status={wsStatus} message={wsMessage} />}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Show Start Review button for draft_ready */}
        {assessment.status === "draft_ready" && (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="flex flex-col items-center gap-3">
                <Clock className="w-12 h-12 text-blue-500" />
                <h3 className="text-lg font-semibold">Ready for Review</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  The AI has generated a draft report. Please review and approve it to send feedback to the student.
                </p>
                <Button
                  onClick={async () => {
                    if (!id) return;
                    try {
                      await startReviewMutation.mutateAsync(id);
                    } catch (error) {
                      console.error("Failed to start review:", error);
                    }
                  }}
                  disabled={startReviewMutation.isPending}
                  className="gap-2"
                >
                  {startReviewMutation.isPending ? "Starting..." : "Start Review"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Show completed message for signed_off */}
        {assessment.status === "signed_off" && (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="flex flex-col items-center gap-3">
                <CheckCircle className="w-12 h-12 text-green-500" />
                <h3 className="text-lg font-semibold">Assessment Completed</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  This assessment has been signed off and the feedback has been sent to the student.
                </p>
                {signedReport?.id && (
                  <Button
                    variant="outline"
                    className="gap-2 mt-2"
                    disabled={downloadPdf.isPending}
                    onClick={() =>
                      downloadPdf.mutate({
                        reportId: signedReport.id,
                        filename: `oracy-report-${assessment.student_name || "student"}.pdf`.replace(/\s+/g, "-"),
                      })
                    }
                  >
                    <Download className="w-4 h-4" />
                    {downloadPdf.isPending ? "Preparing PDF..." : "Download PDF"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Only show content when under_review or signed_off */}
        {["under_review", "signed_off"].includes(assessment.status) && (
        <>
        {/* Audio player with transcript */}
        <Card>
          <CardContent className="p-4">
            {loadingRecording ? (
              <p className="text-sm text-muted-foreground">Loading recording...</p>
            ) : resolvedRecordingUrl ? (
              <>
                {isVideoSource ? (
                  <video
                    ref={(element) => {
                      mediaRef.current = element;
                    }}
                    className="hidden"
                    preload="auto"
                    crossOrigin="anonymous"
                    onTimeUpdate={(event) =>
                      setCurrentTime((event.currentTarget as HTMLMediaElement).currentTime)
                    }
                    onLoadedMetadata={(event) => {
                      const target = event.currentTarget as HTMLMediaElement;
                      ensureFiniteDuration(target, setDuration);
                      setPlaybackError(null);
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                    onError={(event) => {
                      const target = event.currentTarget as HTMLMediaElement;
                      const error = target.error;
                      console.error("Recording failed to load:", error);
                      setIsPlaying(false);
                      setPlaybackError("Recording failed to load. You can open it in a new tab below.");
                    }}
                  >
                    <source src={resolvedRecordingUrl ?? undefined} type={mediaMimeType ?? undefined} />
                    Your browser does not support video playback.
                  </video>
                ) : (
                  <audio
                    ref={(element) => {
                      mediaRef.current = element;
                    }}
                    className="hidden"
                    preload="auto"
                    crossOrigin="anonymous"
                    onTimeUpdate={(event) =>
                      setCurrentTime((event.currentTarget as HTMLMediaElement).currentTime)
                    }
                    onLoadedMetadata={(event) => {
                      const target = event.currentTarget as HTMLMediaElement;
                      ensureFiniteDuration(target, setDuration);
                      setPlaybackError(null);
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                    onError={(event) => {
                      const target = event.currentTarget as HTMLMediaElement;
                      const error = target.error;
                      console.error("Recording failed to load:", error);
                      setIsPlaying(false);
                      setPlaybackError("Recording failed to load. You can open it in a new tab below.");
                    }}
                  >
                    <source src={resolvedRecordingUrl ?? undefined} type={mediaMimeType ?? undefined} />
                    Your browser does not support audio playback.
                  </audio>
                )}
                {/* Custom audio UI */}
                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0 gap-1"
                    onClick={handlePlayPause}
                  >
                    {isPlaying ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    {isPlaying ? "Pause" : "Play"}
                  </Button>
                  <div
                    className="flex-1 h-10 bg-secondary rounded-lg flex items-center gap-0.5 px-3 cursor-pointer relative overflow-hidden"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const percent = (e.clientX - rect.left) / rect.width;
                      handleSeek(percent * duration);
                    }}
                  >
                    <div
                      className="absolute left-0 top-0 bottom-0 bg-primary/20 transition-all"
                      style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                    />
                    {Array.from({ length: 60 }).map((_, i) => {
                      const isPlayed = i < (currentTime / duration) * 60;
                      return (
                        <div
                          key={i}
                          className={`w-1 rounded-full flex-1 ${isPlayed ? "bg-primary" : "bg-primary/30"}`}
                          style={{ height: `${Math.max(4, 20 + (i % 7) * 12)}%` }}
                        />
                      );
                    })}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                    {formatTime(currentTime)} / {formatTime(duration || 0)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {resolvedRecordingUrl && (
                    <Button asChild variant="ghost" size="sm" className="px-2">
                      <a href={resolvedRecordingUrl} target="_blank" rel="noopener noreferrer">
                        {isVideoSource ? "Open video in new tab" : "Open audio in new tab"}
                      </a>
                    </Button>
                  )}
                  {playbackError && (
                    <span className="text-xs text-destructive">{playbackError}</span>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No recording available</p>
            )}
          </CardContent>
        </Card>

        {/* Transcript Viewer */}
        {transcript && transcript.segments && transcript.segments.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-4">Transcript</h2>
            <TranscriptViewer
              segments={transcript.segments}
              currentTime={currentTime}
              onSeek={handleSeek}
              isPlaying={isPlaying}
            />
          </div>
        )}

        {/* Timestamped evidence */}
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">AI-Generated Evidence</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Review the AI's analysis below. Click timestamps to jump to that moment in the recording.
          </p>
          {loadingEvidence ? (
            <Card>
              <CardContent className="p-4 text-center text-muted-foreground">
                Loading evidence...
              </CardContent>
            </Card>
          ) : evidence && evidence.length > 0 ? (
            <div className="space-y-3">
              {evidence.map((ev, i) => (
                <Card key={i} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1.5">
                          <button className="flex items-center gap-1 text-xs text-primary font-mono hover:underline">
                            <Clock className="w-3 h-3" /> 
                            {formatTime(ev.start_time)}–{formatTime(ev.end_time)}
                          </button>
                          <StrandBadge strand={(ev.relevant_strands[0] as "physical" | "linguistic" | "cognitive" | "social-emotional") || "cognitive"} />
                        </div>
                        <p className="text-sm text-foreground">{ev.summary}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-4 text-center text-muted-foreground">
                No evidence available yet
              </CardContent>
            </Card>
          )}
        </div>

        {/* Teacher scoring */}
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">Confirm / Edit Scores</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {scores.map((s, i) => {
              const isValidated = s.strand === "physical";
              return (
                <Card key={s.strand} className={!isValidated ? "opacity-60" : ""}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <StrandBadge strand={s.strand === "social_emotional" ? "social-emotional" : s.strand as "physical" | "linguistic" | "cognitive"} />
                      {isValidated ? (
                        <span className="text-xs text-muted-foreground">AI: {s.aiScore}/5</span>
                      ) : (
                        <span className="text-xs text-amber-600 font-medium">Coming Soon</span>
                      )}
                    </div>
                    {isValidated ? (
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            onClick={() => updateScore(i, n)}
                            className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                              (s.teacherScore ?? s.aiScore) === n
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "bg-secondary text-muted-foreground hover:bg-accent"
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                        {s.teacherScore !== null && s.teacherScore !== s.aiScore && (
                          <span className="ml-2 text-xs text-warning flex items-center gap-1">
                            <Edit3 className="w-3 h-3" /> Edited
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Scoring model under development</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Confirm button */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline">Add Teacher Note</Button>
          <Button
            onClick={handleSignOff}
            disabled={confirmed || signOffMutation.isPending}
            className="gap-2"
          >
            {signOffMutation.isPending ? (
              "Signing off..."
            ) : confirmed ? (
              <>
                <CheckCircle className="w-4 h-4" /> Confirmed
              </>
            ) : (
              "Confirm & Send Feedback"
            )}
          </Button>
        </div>
        </>
        )}
      </div>
    </AppLayout>
  );
}
